import { secondsToFrame } from '../model/time.ts';
import type { Clip, Id, Project, Sequence, Track, Transition } from '../model/schema.ts';

export interface ClipLocation {
  sequence: Sequence;
  track: Track;
  clip: Clip;
  index: number;
}

export function clipEnd(clip: Pick<Clip, 'start' | 'duration'>): number {
  return clip.start + clip.duration;
}

export function allTracks(seq: Sequence): Track[] {
  return [...seq.videoTracks, ...seq.audioTracks];
}

export function getSequence(project: Project, id: Id): Sequence {
  const seq = project.sequences[id];
  if (!seq) throw new Error(`Unknown sequence ${id}`);
  return seq;
}

export function findTrack(project: Project, trackId: Id): { sequence: Sequence; track: Track } | null {
  for (const sequence of Object.values(project.sequences)) {
    const track = allTracks(sequence).find((t) => t.id === trackId);
    if (track) return { sequence, track };
  }
  return null;
}

export function getTrack(project: Project, trackId: Id): { sequence: Sequence; track: Track } {
  const found = findTrack(project, trackId);
  if (!found) throw new Error(`Unknown track ${trackId}`);
  return found;
}

export function findClip(project: Project, clipId: Id): ClipLocation | null {
  for (const sequence of Object.values(project.sequences)) {
    for (const track of allTracks(sequence)) {
      const index = track.clips.findIndex((c) => c.id === clipId);
      if (index !== -1) return { sequence, track, clip: track.clips[index]!, index };
    }
  }
  return null;
}

export function getClip(project: Project, clipId: Id): ClipLocation {
  const loc = findClip(project, clipId);
  if (!loc) throw new Error(`Unknown clip ${clipId}`);
  return loc;
}

/** Index of the clip covering `frame` on a track (clips are sorted and non-overlapping). */
export function clipIndexAt(track: Track, frame: number): number {
  let lo = 0;
  let hi = track.clips.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const c = track.clips[mid]!;
    if (frame < c.start) hi = mid - 1;
    else if (frame >= clipEnd(c)) lo = mid + 1;
    else return mid;
  }
  return -1;
}

export function clipAt(track: Track, frame: number): Clip | undefined {
  const i = clipIndexAt(track, frame);
  return i === -1 ? undefined : track.clips[i];
}

/** Duration of a sequence in frames: the end of its last clip. */
export function sequenceDuration(project: Project, seqOrId: Sequence | Id): number {
  const seq = typeof seqOrId === 'string' ? getSequence(project, seqOrId) : seqOrId;
  let end = 0;
  for (const t of allTracks(seq)) {
    const last = t.clips[t.clips.length - 1];
    if (last) end = Math.max(end, clipEnd(last));
  }
  return end;
}

/**
 * Maximum timeline duration a clip may have given its source. `Infinity` for generated content.
 * Accounts for `sourceIn` and playback speed.
 */
export function maxClipDuration(project: Project, clip: Clip): number {
  const fps = project.settings.fps;
  switch (clip.type) {
    case 'video':
    case 'audio': {
      const media = project.media[clip.mediaId];
      if (!media?.duration) return Infinity;
      return Math.max(1, secondsToFrame((media.duration - clip.sourceIn) / clip.speed, fps));
    }
    case 'sequence':
      return Math.max(1, sequenceDuration(project, clip.sequenceId) - clip.sourceIn);
    default:
      return Infinity;
  }
}

/** Frames of source available before the clip's in-point (for trimming the head outwards). */
export function headroom(project: Project, clip: Clip): number {
  switch (clip.type) {
    case 'video':
    case 'audio':
      return secondsToFrame(clip.sourceIn / clip.speed, project.settings.fps);
    case 'sequence':
      return clip.sourceIn;
    default:
      return Infinity;
  }
}

/** Sequences referenced (directly or transitively) by a sequence. */
export function nestedSequenceIds(project: Project, seqId: Id, acc = new Set<Id>()): Set<Id> {
  const seq = project.sequences[seqId];
  if (!seq) return acc;
  for (const t of seq.videoTracks.concat(seq.audioTracks)) {
    for (const c of t.clips) {
      if (c.type === 'sequence' && !acc.has(c.sequenceId)) {
        acc.add(c.sequenceId);
        nestedSequenceIds(project, c.sequenceId, acc);
      }
    }
  }
  return acc;
}

/** True if placing `childId` inside `parentId` would create a cycle. */
export function wouldCreateCycle(project: Project, parentId: Id, childId: Id): boolean {
  return parentId === childId || nestedSequenceIds(project, childId).has(parentId);
}

/** Sequences that contain a clip referencing `seqId`. */
export function sequenceUsages(project: Project, seqId: Id): Array<{ sequence: Sequence; clip: Clip }> {
  const out: Array<{ sequence: Sequence; clip: Clip }> = [];
  for (const sequence of Object.values(project.sequences)) {
    for (const t of allTracks(sequence)) {
      for (const clip of t.clips) {
        if (clip.type === 'sequence' && clip.sequenceId === seqId) out.push({ sequence, clip });
      }
    }
  }
  return out;
}

export function mediaUsageCount(project: Project, mediaId: Id): number {
  return mediaUsageCounts(project).get(mediaId) ?? 0;
}

/** Number of clips (in any sequence) using each media asset; unused media is absent. */
export function mediaUsageCounts(project: Project): Map<Id, number> {
  const counts = new Map<Id, number>();
  for (const sequence of Object.values(project.sequences)) {
    for (const t of allTracks(sequence)) {
      for (const c of t.clips) if ('mediaId' in c) counts.set(c.mediaId, (counts.get(c.mediaId) ?? 0) + 1);
    }
  }
  return counts;
}

/** Timeline span `[start, end)` covered by a transition. */
export function transitionRange(
  track: Track,
  tr: Transition,
): { start: number; end: number; cut: number } | null {
  const from = track.clips.find((c) => c.id === tr.fromClipId);
  const to = track.clips.find((c) => c.id === tr.toClipId);
  if (!from || !to || clipEnd(from) !== to.start) return null;
  const cut = to.start;
  const start = cut - Math.floor(tr.duration / 2);
  return { start, end: start + tr.duration, cut };
}

/** All "edit points" of a sequence, used for snapping and next/previous-edit navigation. */
export function editPoints(seq: Sequence): number[] {
  const set = new Set<number>([0]);
  for (const t of allTracks(seq)) {
    for (const c of t.clips) {
      set.add(c.start);
      set.add(clipEnd(c));
    }
  }
  for (const m of seq.markers) set.add(m.frame);
  return [...set].sort((a, b) => a - b);
}
